import { CalculatorInputs } from "@/components/CalculatorForm";

export const calculateHiringResults = (inputs: CalculatorInputs) => {
  console.log("Monthly recruiting goals per recruiter:", inputs.recruitingGoals);
  console.log("Current recruiting team:", inputs.recruitingTeam.currentRecruiters);

  // Calculate total hires needed for each department
  const totalHiresNeeded = {
    gtm: inputs.hiringData.gtm.incrementalHires + Math.ceil(inputs.hiringData.gtm.startingHeadcount * (inputs.hiringData.gtm.backfillPercentage / 100)),
    gna: inputs.hiringData.gna.incrementalHires + Math.ceil(inputs.hiringData.gna.startingHeadcount * (inputs.hiringData.gna.backfillPercentage / 100)),
    tech: inputs.hiringData.tech.incrementalHires + Math.ceil(inputs.hiringData.tech.startingHeadcount * (inputs.hiringData.tech.backfillPercentage / 100)),
    exec: inputs.hiringData.exec.incrementalHires + Math.ceil(inputs.hiringData.exec.startingHeadcount * (inputs.hiringData.exec.backfillPercentage / 100)),
    total: 0,
  };

  // Calculate total
  totalHiresNeeded.total = Object.values(totalHiresNeeded).reduce(
    (sum, val) => (typeof val === 'number' ? sum + val : sum),
    0
  );

  console.log("Total hires needed per department:", totalHiresNeeded);

  // Calculate effective hiring months (excluding ramp period)
  const effectiveHiringMonths = Math.max(1, inputs.timelineMonths - inputs.rampPeriodMonths);

  console.log("Effective hiring months:", effectiveHiringMonths);

  // Calculate current team's monthly hiring capacity
  const currentTeamMonthlyCapacity = {
    gtm: inputs.recruitingTeam.currentRecruiters.gtm * inputs.recruitingGoals.gtm,
    gna: inputs.recruitingTeam.currentRecruiters.gna * inputs.recruitingGoals.gna,
    tech: inputs.recruitingTeam.currentRecruiters.tech * inputs.recruitingGoals.tech,
    exec: inputs.recruitingTeam.currentRecruiters.exec * inputs.recruitingGoals.exec,
  };

  console.log("Current team monthly hiring capacity:", currentTeamMonthlyCapacity);

  // Calculate total hires by current team over timeline
  const currentTeamTotalHires = {
    gtm: currentTeamMonthlyCapacity.gtm * inputs.timelineMonths,
    gna: currentTeamMonthlyCapacity.gna * inputs.timelineMonths,
    tech: currentTeamMonthlyCapacity.tech * inputs.timelineMonths,
    exec: currentTeamMonthlyCapacity.exec * inputs.timelineMonths,
  };

  console.log("Current team total hires over timeline:", currentTeamTotalHires);

  // Calculate remaining hires needed after current team capacity
  const remainingHiresNeeded = {
    gtm: Math.max(0, totalHiresNeeded.gtm - currentTeamTotalHires.gtm),
    gna: Math.max(0, totalHiresNeeded.gna - currentTeamTotalHires.gna),
    tech: Math.max(0, totalHiresNeeded.tech - currentTeamTotalHires.tech),
    exec: Math.max(0, totalHiresNeeded.exec - currentTeamTotalHires.exec),
  };

  console.log("Remaining hires needed after current team capacity:", remainingHiresNeeded);

  // Calculate additional recruiters needed for each department using the same logic as DepartmentCard
  const additionalRecruitersNeeded = {
    gtm: inputs.recruitingGoals.gtm > 0 
      ? Math.ceil(remainingHiresNeeded.gtm / (inputs.recruitingGoals.gtm * effectiveHiringMonths))
      : 0,
    gna: inputs.recruitingGoals.gna > 0 
      ? Math.ceil(remainingHiresNeeded.gna / (inputs.recruitingGoals.gna * effectiveHiringMonths))
      : 0,
    tech: inputs.recruitingGoals.tech > 0 
      ? Math.ceil(remainingHiresNeeded.tech / (inputs.recruitingGoals.tech * effectiveHiringMonths))
      : 0,
    exec: inputs.recruitingGoals.exec > 0 
      ? Math.ceil(remainingHiresNeeded.exec / (inputs.recruitingGoals.exec * effectiveHiringMonths))
      : 0
  };

  console.log("Additional recruiters needed per department:", additionalRecruitersNeeded);

  // Calculate monthly hiring pace based on recruiting goals and additional recruiters
  const monthlyHiringPace = {
    gtm: additionalRecruitersNeeded.gtm * inputs.recruitingGoals.gtm,
    gna: additionalRecruitersNeeded.gna * inputs.recruitingGoals.gna,
    tech: additionalRecruitersNeeded.tech * inputs.recruitingGoals.tech,
    exec: additionalRecruitersNeeded.exec * inputs.recruitingGoals.exec,
    total: 0,
  };

  monthlyHiringPace.total = Object.values(monthlyHiringPace).reduce(
    (sum, val) => (typeof val === 'number' ? sum + val : sum),
    0
  );

  console.log("Monthly hiring pace (based on recruiting goals):", monthlyHiringPace);

  // Generate timeline data
  const timelineData = Array.from({ length: inputs.timelineMonths }, (_, index) => {
    const currentTeamHiresThisMonth = {
      gtm: currentTeamMonthlyCapacity.gtm,
      gna: currentTeamMonthlyCapacity.gna,
      tech: currentTeamMonthlyCapacity.tech,
      exec: currentTeamMonthlyCapacity.exec,
    };

    // New recruiters only start hiring after ramp period
    const newTeamHiresThisMonth = {
      gtm: index >= inputs.rampPeriodMonths ? monthlyHiringPace.gtm : 0,
      gna: index >= inputs.rampPeriodMonths ? monthlyHiringPace.gna : 0,
      tech: index >= inputs.rampPeriodMonths ? monthlyHiringPace.tech : 0,
      exec: index >= inputs.rampPeriodMonths ? monthlyHiringPace.exec : 0,
    };

    // Total hires this month combines current team and new team
    const totalHiresThisMonth = {
      gtm: currentTeamHiresThisMonth.gtm + newTeamHiresThisMonth.gtm,
      gna: currentTeamHiresThisMonth.gna + newTeamHiresThisMonth.gna,
      tech: currentTeamHiresThisMonth.tech + newTeamHiresThisMonth.tech,
      exec: currentTeamHiresThisMonth.exec + newTeamHiresThisMonth.exec,
    };

    const total = Object.values(totalHiresThisMonth).reduce((sum, val) => sum + val, 0);

    console.log(`Month ${index + 1} hiring breakdown:`, {
      currentTeam: currentTeamHiresThisMonth,
      newTeam: newTeamHiresThisMonth,
      total: totalHiresThisMonth
    });

    return {
      name: `Month ${index + 1}`,
      ...totalHiresThisMonth,
      total,
    };
  });

  console.log("Generated timeline data:", timelineData);

  return {
    totalHiresNeeded,
    monthlyHiringPace,
    timelineData,
    recruitingGoals: inputs.recruitingGoals,
    totalRecruitersNeeded: Object.values(additionalRecruitersNeeded).reduce((sum, val) => sum + val, 0),
    ftePercentage: inputs.recruitingTeam.fteHeadcount,
    outsourcedPercentage: inputs.recruitingTeam.outsourcedHeadcount,
    currentRecruiters: inputs.recruitingTeam.currentRecruiters,
    rampPeriodMonths: inputs.rampPeriodMonths,
    timelineMonths: inputs.timelineMonths,
  };
};

// Helper function to calculate recruiters needed using the same logic as DepartmentCard
const calculateDepartmentRecruiters = (
  totalHires: number,
  currentRecruiters: number,
  monthlyGoal: number,
  timelineMonths: number,
  rampPeriodMonths: number
) => {
  // Calculate total team capacity over timeline
  const totalTeamCapacity = currentRecruiters * monthlyGoal * timelineMonths;
  
  // Calculate shortfall
  const shortfall = Math.max(0, totalHires - totalTeamCapacity);
  
  // Calculate effective hiring months
  const effectiveHiringMonths = Math.max(1, timelineMonths - rampPeriodMonths);
  
  // Calculate additional recruiters needed
  return monthlyGoal > 0 ? Math.ceil(shortfall / (monthlyGoal * effectiveHiringMonths)) : 0;
};

export const getRecruitersNeeded = (inputs: CalculatorInputs): number => {
  // Calculate department-specific recruiters needed using the same logic as DepartmentCard
  const techRecruiters = calculateDepartmentRecruiters(
    inputs.hiringData.tech.plannedGrossHires,
    inputs.recruitingTeam.currentRecruiters.tech,
    inputs.recruitingGoals.tech,
    inputs.timelineMonths,
    inputs.rampPeriodMonths
  );

  const gtmRecruiters = calculateDepartmentRecruiters(
    inputs.hiringData.gtm.plannedGrossHires,
    inputs.recruitingTeam.currentRecruiters.gtm,
    inputs.recruitingGoals.gtm,
    inputs.timelineMonths,
    inputs.rampPeriodMonths
  );

  const gnaRecruiters = calculateDepartmentRecruiters(
    inputs.hiringData.gna.plannedGrossHires,
    inputs.recruitingTeam.currentRecruiters.gna,
    inputs.recruitingGoals.gna,
    inputs.timelineMonths,
    inputs.rampPeriodMonths
  );

  const execRecruiters = calculateDepartmentRecruiters(
    inputs.hiringData.exec.plannedGrossHires,
    inputs.recruitingTeam.currentRecruiters.exec,
    inputs.recruitingGoals.exec,
    inputs.timelineMonths,
    inputs.rampPeriodMonths
  );

  console.log('Recruiters needed per department:', {
    tech: techRecruiters,
    gtm: gtmRecruiters,
    gna: gnaRecruiters,
    exec: execRecruiters,
    explanation: 'Using consistent calculation across all departments'
  });

  // Sum up all recruiters needed
  const totalRecruiters = techRecruiters + gtmRecruiters + gnaRecruiters + execRecruiters;

  console.log('Total recruiters needed:', totalRecruiters);

  return totalRecruiters;
};

export const formatCalculationData = (data: CalculatorInputs) => {
  return {
    hiringData: {
      tech: { ...data.hiringData.tech },
      gtm: { ...data.hiringData.gtm },
      gna: { ...data.hiringData.gna },
      exec: { ...data.hiringData.exec },
    },
    timelineMonths: data.timelineMonths,
    rampPeriodMonths: data.rampPeriodMonths,
    recruitingTeam: { ...data.recruitingTeam },
    recruitingGoals: { ...data.recruitingGoals },
  };
};